/**
 * ErrorBoundary component for component-level error catching
 * Requirements: 1.4, 3.4, 4.4
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import RefreshIcon from '@mui/icons-material/Refresh';
import BugReportIcon from '@mui/icons-material/BugReport';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  resetOnPropsChange?: boolean;
  resetKeys?: Array<string | number>;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string;
}

/**
 * Error boundary component that catches JavaScript errors anywhere in the child component tree
 * Provides user-friendly error messages and recovery options
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private resetTimeoutId: number | null = null;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: '',
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorId: `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error details
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
    });

    // Call optional error handler
    this.props.onError?.(error, errorInfo);

    // Log to external error reporting service if available
    this.logErrorToService(error, errorInfo);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    const { resetOnPropsChange, resetKeys } = this.props;
    const { hasError } = this.state;

    // Reset error state when resetKeys change
    if (hasError && resetOnPropsChange && resetKeys) {
      const prevResetKeys = prevProps.resetKeys || [];
      const hasResetKeyChanged = resetKeys.some(
        (key, index) => key !== prevResetKeys[index]
      );

      if (hasResetKeyChanged) {
        this.resetErrorBoundary();
      }
    }
  }

  componentWillUnmount() {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
    }
  }

  private logErrorToService = (error: Error, errorInfo: ErrorInfo) => {
    // In a real application, you would send this to an error reporting service
    // like Sentry, LogRocket, or Azure Application Insights
    const errorReport = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
    };

    console.log('Error report:', errorReport);
    
    // Example: Send to Azure Application Insights
    // if (window.appInsights) {
    //   window.appInsights.trackException({ exception: error, properties: errorReport });
    // }
  };

  private resetErrorBoundary = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: '',
    });
  };

  private handleRetry = () => {
    this.resetErrorBoundary();
  };

  private handleReload = () => {
    window.location.reload();
  };

  private getErrorMessage = (error: Error): string => {
    // Provide user-friendly error messages based on error type
    if (error.name === 'ChunkLoadError') {
      return 'Failed to load application resources. Please refresh the page.';
    }
    
    if (error.message.includes('PDF')) {
      return 'There was a problem loading the PDF document. Please try again or contact support if the issue persists.';
    }
    
    if (error.message.includes('Network')) {
      return 'Network connection error. Please check your internet connection and try again.';
    }
    
    if (error.message.includes('coordinate') || error.message.includes('highlight')) {
      return 'There was a problem displaying document highlights. The document is still accessible, but highlighting may not work correctly.';
    }
    
    if (error.message.includes('validation')) {
      return 'There was a problem with form validation. Please check your input and try again.';
    }

    // Generic error message
    return 'An unexpected error occurred. Please try again or contact support if the problem continues.';
  };

  private renderErrorDetails = () => {
    const { error, errorInfo } = this.state;
    
    if (!error) return null;

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Error Details (for technical support):
        </Typography>
        <Box
          component="pre"
          sx={{
            fontSize: '0.75rem',
            backgroundColor: 'grey.100',
            p: 1,
            borderRadius: 1,
            overflow: 'auto',
            maxHeight: 200,
            border: 1,
            borderColor: 'grey.300',
          }}
        >
          {error.message}
          {process.env.NODE_ENV === 'development' && errorInfo && (
            <>
              {'\n\nComponent Stack:'}
              {errorInfo.componentStack}
            </>
          )}
        </Box>
      </Box>
    );
  };

  render() {
    const { hasError, error } = this.state;
    const { children, fallback } = this.props;

    if (hasError) {
      // Custom fallback UI
      if (fallback) {
        return fallback;
      }

      // Default error UI
      return (
        <Box
          sx={{
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 300,
            textAlign: 'center',
          }}
        >
          <Alert
            severity="error"
            icon={<BugReportIcon />}
            sx={{ width: '100%', maxWidth: 600, mb: 2 }}
          >
            <AlertTitle>Something went wrong</AlertTitle>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {error ? this.getErrorMessage(error) : 'An unexpected error occurred.'}
            </Typography>
            
            <Stack direction="row" spacing={2} justifyContent="center">
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={this.handleRetry}
                size="small"
              >
                Try Again
              </Button>
              <Button
                variant="outlined"
                onClick={this.handleReload}
                size="small"
              >
                Reload Page
              </Button>
            </Stack>
          </Alert>

          {process.env.NODE_ENV === 'development' && this.renderErrorDetails()}
        </Box>
      );
    }

    return children;
  }
}

/**
 * Hook for creating error boundaries with automatic reset
 */
export function useErrorBoundary() {
  const [error, setError] = React.useState<Error | null>(null);

  const resetError = React.useCallback(() => {
    setError(null);
  }, []);

  const captureError = React.useCallback((error: Error) => {
    setError(error);
  }, []);

  React.useEffect(() => {
    if (error) {
      throw error;
    }
  }, [error]);

  return { captureError, resetError };
}

export default ErrorBoundary;